/**
 * Catches a screen that throws while rendering, so one broken screen leaves
 * the navigation usable instead of blanking the whole app.
 *
 * One failure gets its own handling: a screen's code is loaded on demand, and
 * after a deploy the file an open tab asks for no longer exists. Reloading
 * fetches the current build, so that is done once automatically; if it fails
 * again (the network is down, say) the reader gets the button instead of a
 * reload loop.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Banner, Button } from "./ui";

const RELOAD_KEY = "lettrova.chunk_reload_at";
const RELOAD_COOLDOWN_MS = 60_000;

/** True for the errors browsers raise when a lazily-loaded script can't be fetched. */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /dynamically imported module|module script failed|Loading chunk|ChunkLoadError|Unable to preload CSS/i.test(message);
}

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    // Without storage there is no way to tell a first failure from a loop.
    return true;
  }
}

interface Props {
  children: ReactNode;
  /** The boundary clears itself when this changes (the route, so navigating away recovers). */
  resetKey?: string;
  reload?: () => void;
}

interface State {
  error: unknown;
  resetKey?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return props.resetKey === state.resetKey ? null : { error: null, resetKey: props.resetKey };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("Screen failed to render", error, info.componentStack);
    if (isChunkLoadError(error) && !reloadedRecently()) {
      try {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      } catch {
        /* unreachable: reloadedRecently() already reported storage as unusable */
      }
      this.reload();
    }
  }

  reload = () => (this.props.reload ?? (() => window.location.reload()))();

  render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    const stale = isChunkLoadError(error);
    return (
      <Banner tone="danger" className="mx-auto max-w-xl">
        <h1 className="font-semibold">{stale ? "This screen couldn't be loaded" : "Something went wrong on this screen"}</h1>
        <p className="mt-1">
          {stale
            ? "Lettrova may have been updated since you opened it, or your connection dropped. Reload to get the current version."
            : "Your stores and campaigns are not affected. Try again, or reload the page if it keeps happening."}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {!stale && (
            <Button variant="secondary" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
          )}
          <Button variant={stale ? "primary" : "secondary"} onClick={this.reload}>
            Reload
          </Button>
        </div>
      </Banner>
    );
  }
}
